import { useQuery } from '@tanstack/react-query';

import { donedealServicesGateway } from '../../../../api/api-utils/commonApiUtils';

import type {
  Filters,
  MakeModelOption,
} from '../../MakeModelKeywordSearch.typed';
import { z } from 'zod';
import { useMemo } from 'react';
import useDebounce from '../../../../hooks/useDebounce';
import { formatNumber } from '../../../../helpers/formatting';

type Data = Array<MakeModelOption>;

/**
 * @param args filter values to be included in search payload
 * @returns autocomplete options, error state and loading state due to network requests
 */
export const useMakeModelKeywordSearch = ({
  maxPrice,
  minPrice,
  maxYear,
  minYear,
  keyword,
  listIsOpen,
}: Filters & { keyword: string; listIsOpen: boolean }): {
  isPending: boolean;
  data: Data | undefined;
  isError: boolean;
  reload: () => void;
} => {
  const debouncedKeyword = useDebounce(keyword, 300);
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [
      'makeModelKeywordSearch',
      { minPrice, maxPrice, minYear, maxYear, keyword: debouncedKeyword },
    ],
    queryFn: () =>
      fetchMakeAndModels({
        minPrice,
        maxPrice,
        minYear,
        maxYear,
        keyword: debouncedKeyword,
      }),
    enabled: Boolean(debouncedKeyword) && listIsOpen,
    retry: false,
  });

  return useMemo(
    () => ({
      isPending,
      data: data?.map((result) => ({
        label: result.displayName,
        value: {
          make: result.keyValues.make,
          model: result.keyValues.model ?? undefined,
        },
        count: String(formatNumber(result.count)),
      })),
      isError,
      reload: refetch,
    }),
    [data, isPending, isError, refetch],
  );
};

const responseSchema = z.object({
  filterValues: z.array(
    z.object({
      keyValues: z.object({
        make: z.string(),
        model: z.string().optional(),
      }),
      displayName: z.string(),
      count: z.number(),
    }),
  ),
});

async function fetchMakeAndModels({
  maxPrice,
  minPrice,
  maxYear,
  minYear,
  keyword,
}: Filters & { keyword: string }) {
  const ranges = [];

  if (maxPrice !== undefined || minPrice !== undefined) {
    ranges.push({
      name: 'price',
      ...(minPrice !== undefined && { from: minPrice }),
      ...(maxPrice !== undefined && { to: maxPrice }),
    });
  }

  if (maxYear !== undefined || minYear !== undefined) {
    ranges.push({
      name: 'year',
      ...(minYear !== undefined && { from: minYear }),
      ...(maxYear !== undefined && { to: maxYear }),
    });
  }

  const response = await donedealServicesGateway.post(
    'ddapi/v4/search/filters/cars/suggestions',
    {
      query: keyword,
      searchApiRequest: {
        filters: [],
        ranges,
      },
    },
  );

  return responseSchema.parse(response.data).filterValues;
}
